/**
 * Helper for formatting input values based on the specified type.
 * Useful for standardizing user input in web applications.
 *
 * Author: Leonardo Salas
 * Email: [Removed for privacy]
 * Copyright: © Livelaps 2023
 * Creation Date: September 2023
 * Version: 1.0.0
 *
 * Usage:
 * const formattedValue = this.$inputFormatter(value, type);
 * @param {string} value - The input value to be formatted.
 * @param {string} type - The type of formatting to apply ('phoneNumber' in this example).
 *
 * Example:
 * Let's say you have an input field for a phone number, and you want to format it as XXX-XXX-XXXX.
 * You can use the helper as follows:
 * const formattedPhoneNumber = this.$inputFormatter('1234567890', 'phoneNumber');
 * // formattedPhoneNumber will contain '123-456-7890'
 *
 * Output Format:
 * The helper returns the formatted value as a string based on the specified type.
 *
 * Notes:
 * - Currently, the helper supports 'phoneNumber' and 'spaceCleaner' type formatting.
 * - You can extend this helper to support additional formatting types as needed.
 * - Make sure to register the helper in your application according to your framework's specifications.
 */

const InputFormatterPlugin = {
    /**
     * Install the inputRules plugin.
     * @param {Object} Vue - The Vue instance.
     */
    install(Vue) {
        Vue.prototype.$inputFormatter = function (value, type) {
            let response;

            switch (type) {
                case "phoneNumber":
                    response = phoneFormat(value);
                    break;

                case "spaceCleaner":
                    response = spaceCleaner(value);
                    break;

                case "address":
                    response = addressFormat(value);
                    break;

                /*case "strapiTextAreaImageSrcFixer":
                    response = strapiTextAreaImageSrcFixer(app.store, value);*/

                default:
                    break;
            }

            return response;
        };

        /**
         * Format a phone number as XXX-XXX-XXXX.
         * @param {string} phone - The input phone number.
         * @returns {string} The formatted phone number.
         */
        function phoneFormat(phone) {
            if (!phone) return "";
            const numbers = phone.replace(/\D/g, "").slice(0, 10);

            // Apply the desired format using capture groups in the regular expression.
            let phoneFormatted;
            switch (numbers.length) {
                case 4:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{1})/, "$1-$2");
                    break;
                case 5:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{2})/, "$1-$2");
                    break;
                case 6:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{3})/, "$1-$2");
                    break;
                case 7:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{3})(\d{1})/, "$1-$2-$3");
                    break;
                case 8:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{3})(\d{2})/, "$1-$2-$3");
                    break;
                case 9:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3");
                    break;
                case 10:
                    phoneFormatted = numbers.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
                    break;
                default:
                    phoneFormatted = numbers;
            }

            return phoneFormatted.length > 12 ? phoneFormatted.substring(0, 12) : phoneFormatted;
        }

        /**
         * Remove all spaces from a string.
         * @param {string} val - The input string with spaces.
         * @returns {string} The string with spaces removed.
         */
        function spaceCleaner(val) {
            return (val || "").replace(/\s/g, "");
        }

        /**
         * Format an address with a maximum length of 99 characters.
         * Remove extra characters and special characters.
         * @param {string} address - The input address.
         * @returns {string} The formatted address.
         */
        function addressFormat(address) {
            if (!address) return "";
            // Remove special characters and limit the address to 99 characters
            const cleanedAddress = address.replace(/[^a-zA-Z0-9\s]/g, "").slice(0, 99);

            return cleanedAddress;
        }

        /*function strapiTextAreaImageSrcFixer(store, htmlContent) {
            // Your custom URL
            const strapiURL = store.getters["env/GET_VUE_APP_STRAPI"];

            // Create a temporary HTML element to parse the HTML content
            const tempElement = document.createElement("div");
            tempElement.innerHTML = htmlContent;

            // Find all img elements
            const imgElements = tempElement.querySelectorAll("img");

            // Loop through img elements and modify the src attribute
            imgElements.forEach((img) => {
                let src = img.getAttribute("src");
                if (!src.startsWith("http://") && !src.startsWith("https://")) {
                    img.setAttribute("src", strapiURL + src);
                }
            });

            // Get the modified HTML
            const modifiedHtmlContent = tempElement.innerHTML;

            return modifiedHtmlContent;
        }*/
    }
};

export default InputFormatterPlugin;
